/**
 * Per-bag weighing: the arithmetic and the entry grammar behind the handheld's unloading screen.
 *
 * seedtrac posts one bag at a time (`tranunldlotwtins.php`) and derives the lot's Actual pair from
 * those rows. nexseed does the same, and this module is the shared half: the API recomputes
 * `document_lines.received_*` from `document_line_bags` with `lineTotalsFromBags`, and the handheld
 * validates what the operator typed with the same functions before it ever leaves the device. One
 * implementation, so a bag that the app accepts is never one the server rejects.
 *
 * Bag weighing is a MOBILE-ONLY capability. The web shows these totals and never writes them.
 */
import { Decimal, toDecimal, ZERO, type DecimalInput } from "./decimal";
import { identifierOf } from "./lot-number";

/**
 * Above this, the entry is refused. seedtrac enforces the same ceiling (`if (tvqtyss < 100)` in
 * ArrActualQtyPostActivity2) but reports it as a bare "Please enter valid QTY", so an operator who
 * fat-fingers a digit has no idea what the screen objected to. We name the limit.
 */
export const BAG_WEIGHT_MAX_KG = new Decimal(100);

/** A weighed bag, as the client holds it before sending and as the API reads it back. */
export interface BagWeights {
  bagNo: number;
  grossQty: DecimalInput;
  tareQty: DecimalInput;
}

/** The derived Actual pair for a lot. Strings, because `numeric(14,3)` wants strings. */
export interface LineTotals {
  receivedBags: number;
  receivedGrossQty: string;
  tareQty: string;
  netQty: string;
}

/**
 * Derive a line's Actual pair from its bags. The single source of `received_*` — no client ever
 * sends these, which is what makes "the server derives the totals" true rather than merely intended.
 */
export function lineTotalsFromBags(bags: readonly BagWeights[]): LineTotals {
  let gross = ZERO;
  let tare = ZERO;
  for (const b of bags) {
    gross = gross.plus(toDecimal(b.grossQty));
    tare = tare.plus(toDecimal(b.tareQty));
  }
  return {
    receivedBags: bags.length,
    receivedGrossQty: gross.toFixed(3),
    tareQty: tare.toFixed(3),
    netQty: gross.minus(tare).toFixed(3),
  };
}

/**
 * The next bag number for a lot: 1-based and gap-free from the operator's point of view, because
 * the sheet heading reads "Bag No.<n> : Enter Weight" and an operator counting sacks expects it to
 * match. Deleting the last bag frees its number for reuse; deleting one from the middle does not
 * renumber the rest, and the unique `(line_id, bag_no)` index is what stops two concurrent adds
 * from claiming the same one.
 */
export function nextBagNo(bags: readonly { bagNo: number }[]): number {
  let max = 0;
  for (const b of bags) if (b.bagNo > max) max = b.bagNo;
  return max + 1;
}

/** What a typed entry means, and how the large display should render it. */
export interface ParsedWeight {
  /** The kilograms the entry denotes, or null when it is not a usable number. */
  kg: Decimal | null;
  /** Canonical 3-decimal rendering for the big readout; "" when `kg` is null. */
  display: string;
}

/**
 * The keypad's weight-entry grammar: what the operator types is kilograms.
 *
 * `45` is 45.000 kg and `12.5` is 12.500 kg. This deliberately drops seedtrac's grams shortcut
 * (three or more bare digits split three from the right, so `12500` read as 12.500): with it, `150`
 * silently meant 0.150 kg, where an operator who typed 150 expects a refused 150 kg bag.
 */
export function parseTypedWeight(raw: string): ParsedWeight {
  const s = raw.trim();
  if (s === "") return { kg: null, display: "" };
  if (!/^\d*\.?\d*$/.test(s) || s === ".") return { kg: null, display: "" };

  const kg = toDecimal(s);
  return { kg, display: kg.toFixed(3) };
}

export type WeightIssueLevel = "error";
export interface WeightIssue {
  level: WeightIssueLevel;
  field: "gross" | "tare";
  message: string;
}

/**
 * Everything wrong with one bag's weights; any issue blocks the Add. seedtrac's "Qty is less then
 * 10" warning is deliberately absent — a genuinely light bag is recorded without comment.
 *
 * The error set mirrors the `document_line_bags` check constraints exactly (gross > 0, tare >= 0,
 * tare < gross), so the handheld never sends something the database will bounce.
 */
export function bagWeightIssues(
  grossKg: DecimalInput,
  tareKg: DecimalInput
): WeightIssue[] {
  const out: WeightIssue[] = [];
  const gross = toDecimal(grossKg);
  const tare = toDecimal(tareKg);

  if (grossKg === null || grossKg === undefined || grossKg === "")
    out.push({ level: "error", field: "gross", message: "Enter a weight." });
  else if (gross.lte(0))
    out.push({
      level: "error",
      field: "gross",
      message: "Weight must be greater than zero.",
    });
  else if (gross.gt(BAG_WEIGHT_MAX_KG))
    out.push({
      level: "error",
      field: "gross",
      message: `A bag cannot weigh more than ${BAG_WEIGHT_MAX_KG.toFixed(3)} kg.`,
    });

  if (tareKg === null || tareKg === undefined || tareKg === "")
    out.push({
      level: "error",
      field: "tare",
      message: "Enter a tare weight.",
    });
  else if (tare.lt(0))
    out.push({
      level: "error",
      field: "tare",
      message: "Tare weight cannot be negative.",
    });
  else if (gross.gt(0) && tare.gte(gross))
    out.push({
      level: "error",
      field: "tare",
      message: "Tare weight must be less than the bag weight.",
    });

  return out;
}

// ------------------------------------------------------- declared-weight matching (S-Capture)

/**
 * Normalise a barcode for uniqueness and matching: case-insensitive, whitespace-free, blank ⇒ null.
 *
 * Null is the point — `document_line_bags.barcode_normalised` is uniquely indexed only WHERE it is
 * not null, so a bag weighed without a barcode never collides with another. Returning "" instead
 * would make every unbarcoded bag collide with every other.
 */
export function normaliseBarcode(
  value: string | null | undefined
): string | null {
  const v = (value ?? "").replace(/\s+/g, "").toUpperCase();
  return v === "" ? null : v;
}

/** A bag as S-Capture declared it: `subsub_records` carries `{ lotno, barcode, weight }` per sack. */
export interface DeclaredBag {
  barcode?: string | null;
  weight: DecimalInput;
}

/** A bag as the handheld weighed it. */
export interface WeighedBag {
  bagNo: number;
  barcode?: string | null;
  grossQty: DecimalInput;
}

export interface BagMatch {
  bagNo: number;
  /** null when no declared bag was left to pair with — an extra sack on the truck. */
  matchedBy: "barcode" | "position" | null;
  declaredQty: string | null;
  weighedQty: string;
  /** weighed − declared, positive when the sack is heavier than declared; null when unmatched. */
  varianceQty: string | null;
  withinTolerance: boolean;
}

export interface DeclaredWeightMatch {
  bags: BagMatch[];
  /** Declared bags no weighed bag claimed — sacks that did not arrive, or were not weighed yet. */
  unmatchedDeclared: number;
  outOfTolerance: number;
}

/**
 * A gram. S-Capture weights and scale readings are both three-decimal, so anything above rounding
 * noise is a real discrepancy worth showing the operator.
 */
export const DECLARED_WEIGHT_TOLERANCE_KG = new Decimal(0.01);

/**
 * Pair what was weighed against what S-Capture declared, barcode first and position second.
 *
 * Barcode first because it is the only identification that survives sacks being unloaded out of
 * order, which is the normal case — a truck is emptied from the tailgate. Position second because
 * most sacks carry no readable barcode and the operator still needs SOME declared figure to see the
 * variance against; a positional pairing is a guess, and `matchedBy` says so, so the UI can render
 * it more weakly than a barcode match.
 *
 * This does not decide anything. It reports, and `arrival_scapture` shows the result beside the
 * running Act./Declared totals. Refusing a bag because it disagrees with a declaration would leave
 * the operator holding a sack with nowhere to record it.
 */
export function matchDeclaredWeights(
  declared: readonly DeclaredBag[],
  weighed: readonly WeighedBag[]
): DeclaredWeightMatch {
  const claimed = new Array<boolean>(declared.length).fill(false);

  // Barcodes that repeat within one declaration identify nothing, so they take no part in pass 1
  // and fall through to positional pairing like an unbarcoded sack.
  const byBarcode = new Map<string, number[]>();
  declared.forEach((d, i) => {
    const b = normaliseBarcode(d.barcode);
    if (b) byBarcode.set(b, [...(byBarcode.get(b) ?? []), i]);
  });
  for (const [b, idx] of byBarcode) if (idx.length > 1) byBarcode.delete(b);

  const order = [...weighed].sort((a, b) => a.bagNo - b.bagNo);
  // How each weighed bag was paired, recorded AS it is paired. Deriving it afterwards from "does
  // this bag have a barcode the declaration knows" would mislabel a barcoded sack whose declared
  // twin was already claimed and which therefore fell through to positional pairing.
  const pairedWith = new Map<
    number,
    { idx: number; by: "barcode" | "position" }
  >();

  for (const w of order) {
    const b = normaliseBarcode(w.barcode);
    if (!b) continue;
    const idx = byBarcode.get(b)?.[0];
    if (idx === undefined || claimed[idx]) continue;
    claimed[idx] = true;
    pairedWith.set(w.bagNo, { idx, by: "barcode" });
  }

  let cursor = 0;
  for (const w of order) {
    if (pairedWith.has(w.bagNo)) continue;
    while (cursor < declared.length && claimed[cursor]) cursor++;
    if (cursor >= declared.length) break;
    claimed[cursor] = true;
    pairedWith.set(w.bagNo, { idx: cursor, by: "position" });
  }

  let outOfTolerance = 0;
  const bags: BagMatch[] = order.map((w) => {
    const pair = pairedWith.get(w.bagNo);
    const gross = toDecimal(w.grossQty);
    if (pair === undefined)
      return {
        bagNo: w.bagNo,
        matchedBy: null,
        declaredQty: null,
        weighedQty: gross.toFixed(3),
        varianceQty: null,
        withinTolerance: false,
      };
    const dq = toDecimal(declared[pair.idx]!.weight);
    const variance = gross.minus(dq);
    const withinTolerance = variance.abs().lte(DECLARED_WEIGHT_TOLERANCE_KG);
    if (!withinTolerance) outOfTolerance++;
    return {
      bagNo: w.bagNo,
      matchedBy: pair.by,
      declaredQty: dq.toFixed(3),
      weighedQty: gross.toFixed(3),
      varianceQty: variance.toFixed(3),
      withinTolerance,
    };
  });

  return {
    bags,
    unmatchedDeclared: claimed.filter((c) => !c).length,
    outOfTolerance,
  };
}

// --------------------------------------------------------------------------- scanning

/** A lot as the scan screen knows it. */
export interface ScanLine {
  id: number;
  /** Every number the lot goes by: provisional, external (Core / Lotgen), vendor. */
  lotNumbers: readonly (string | null | undefined)[];
  /** The sacks the feed declared for this lot, when it declared any (S-Capture does). */
  declaredBags?: readonly DeclaredBag[] | null | undefined;
}

export type ScanMatch =
  | { lineId: number; kind: "bag"; barcode: string; declaredWeight: string }
  | { lineId: number; kind: "lot"; barcode: null; declaredWeight: null };

/**
 * What a scanned label is, on this arrival.
 *
 *  1. One of the sacks a lot was declared with. S-Capture labels every sack with its own barcode,
 *     so the scan names the sack AND its lot, and the barcode is kept on the bag.
 *  2. A lot's own number. A Trading sack carries its lot number, the same on every sack of that
 *     lot, so the scan names the lot only; the label is not a bag barcode and is not kept.
 *  3. Neither: the label is not on this truck, and the handheld refuses it.
 *
 * Decided by the data, never the form code: a lot with a declared bag list is matched sack by sack
 * whichever form it sits on, and one without is matched by its number. Sacks are tried first, so a
 * code that happened to be both could never be mistaken for a whole lot.
 */
export function resolveScan(
  lines: readonly ScanLine[],
  code: string | null | undefined
): ScanMatch | null {
  const scanned = normaliseBarcode(code);
  if (scanned === null) return null;
  for (const l of lines)
    for (const bag of l.declaredBags ?? [])
      if (normaliseBarcode(bag.barcode) === scanned)
        return {
          lineId: l.id,
          kind: "bag",
          barcode: scanned,
          declaredWeight: toDecimal(bag.weight).toFixed(3),
        };
  // A lot label may be printed in the complete format; both sides compare on the identifier.
  const lot = normaliseBarcode(identifierOf(scanned));
  for (const l of lines)
    for (const n of l.lotNumbers)
      if (n && normaliseBarcode(identifierOf(n)) === lot)
        return {
          lineId: l.id,
          kind: "lot",
          barcode: null,
          declaredWeight: null,
        };
  return null;
}

// --------------------------------------------------------------------------- the scale

/** How long a reading must hold before it counts as the sack's weight. */
export const STABLE_WINDOW_MS = 1000;
/** How far readings may wander within that window and still be one weight: 10 g. */
export const STABLE_TOLERANCE_KG = 0.01;
/** At or below this the platform is empty: the sack is off, and the next one may fill again. */
export const SCALE_EMPTY_KG = 0.05;

export interface StableState {
  anchorKg: number | null;
  since: number;
  fired: boolean;
}

export const STABLE_START: StableState = {
  anchorKg: null,
  since: 0,
  fired: false,
};

/**
 * Feed one scale reading; get back whether the weight just became stable.
 *
 * A sack landing on the platform swings the reading for a moment. The weight counts once readings
 * have stayed within 10 g of where they settled for a full second, and it fills ONCE per sack: it
 * does not fire again while the same sack sits there, and re-arms only when the platform empties
 * or the load moves by more than the tolerance (a different sack). A pure reducer, so the rule is
 * tested here rather than trusted to a timer on the device.
 */
export function nextStableReading(
  state: StableState,
  kg: number,
  at: number
): { state: StableState; stableKg: string | null } {
  if (!Number.isFinite(kg) || kg <= SCALE_EMPTY_KG)
    return { state: STABLE_START, stableKg: null };
  if (
    state.anchorKg === null ||
    Math.abs(kg - state.anchorKg) > STABLE_TOLERANCE_KG + 1e-9
  )
    return { state: { anchorKg: kg, since: at, fired: false }, stableKg: null };
  if (!state.fired && at - state.since >= STABLE_WINDOW_MS)
    return { state: { ...state, fired: true }, stableKg: kg.toFixed(3) };
  return { state, stableKg: null };
}

/** A reading older than this says nothing about what is on the platform now. */
export const SEED_FRESH_MS = 1500;

/** What the scale has been doing while no weigh screen was listening. */
export interface ScaleTrack {
  /** `nextStableReading` run over every reading; only its anchor and `since` are used. */
  state: StableState;
  lastKg: number | null;
  lastAt: number;
  /**
   * A weigh screen already took this load's weight (the tracker's current anchor). The previous
   * sack still on the platform when the next dialog opens is not the next bag: it must not fill.
   */
  consumed?: boolean;
}

/**
 * Where a weigh screen that has just opened should start settling from.
 *
 * The sack is usually on the platform before its label is scanned, so by the time the dialog
 * mounts the scale may already have held it steady for a second. Starting from scratch made the
 * operator stare at 0.000 while the window ran again. Instead: a load that has already held for the
 * full window fills at once, one still settling carries the time it has already held, and a stale
 * or empty scale starts afresh.
 */
export function seedStableState(
  track: ScaleTrack,
  now: number
): { state: StableState; stableKg: string | null } {
  const { state, lastKg, lastAt } = track;
  if (
    lastKg === null ||
    !Number.isFinite(lastKg) ||
    lastKg <= SCALE_EMPTY_KG ||
    now - lastAt > SEED_FRESH_MS ||
    state.anchorKg === null
  )
    return { state: STABLE_START, stableKg: null };
  const carried = { anchorKg: state.anchorKg, since: state.since };
  // Already used for a bag: armed as fired, so it fills only once the load changes or empties.
  if (track.consumed === true)
    return { state: { ...carried, fired: true }, stableKg: null };
  if (lastAt - state.since >= STABLE_WINDOW_MS)
    return {
      state: { ...carried, fired: true },
      stableKg: lastKg.toFixed(3),
    };
  return { state: { ...carried, fired: false }, stableKg: null };
}

/** A lot with fewer bags weighed than its DC / PDN / dispatch declared. */
export interface LotBagsLeft {
  lot: string;
  left: number;
}

/**
 * Lots still short of their declared bag count. Preview Unloading waits for every declared bag on
 * every bag-wise arrival, online and offline (user, 2026-09-23): a truck that really came short has
 * its declared count corrected first. Extra bags are fine; a lot with no declared count is not held.
 */
export function bagsLeft(
  lines: readonly {
    lot: string;
    declared: number | null | undefined;
    received: number | null | undefined;
  }[]
): LotBagsLeft[] {
  return lines.flatMap((l) => {
    const left = (l.declared ?? 0) - (l.received ?? 0);
    return l.declared !== null && l.declared !== undefined && left > 0
      ? [{ lot: l.lot, left }]
      : [];
  });
}

/** "2 bags are left of lot Y12345" / "…, 1 bag of lot R00012". */
export function bagsLeftMessage(left: readonly LotBagsLeft[]): string {
  const bags = (n: number) => `${n} ${n === 1 ? "bag" : "bags"}`;
  const [first, ...rest] = left;
  if (first === undefined) return "";
  return [
    `${bags(first.left)} ${first.left === 1 ? "is" : "are"} left of lot ${first.lot}`,
    ...rest.map((l) => `${bags(l.left)} of lot ${l.lot}`),
  ].join(", ");
}

/**
 * A recorded weight and the scale's reading further apart than this are a mismatch on the handheld's
 * Check weights screen: the row turns red so the wrong sack stands out. Closer than this, the bag is
 * taken to match and nothing nags the operator to reweigh it.
 */
export const REWEIGH_MISMATCH_KG = new Decimal(0.05);

/**
 * One bag weighed again on a lot the desk sent back for reweigh: what it weighed before, what the
 * scale read this time, and who reweighed it. `change` is the bag's NET change, signed.
 */
export interface BagReweigh {
  grossBefore: string;
  tareBefore: string;
  grossAfter: string;
  tareAfter: string;
  change: string;
  by: string;
  /** ISO instant. */
  at: string;
  /** The send-back this reweigh answered: its reason and instant, when known. */
  sentBackReason: string | null;
  sentBackAt: string | null;
}

/** A bag's net change on a reweigh, signed, to 3 dp: (gross − tare) after minus before. */
export function reweighChange(
  before: { gross: DecimalInput; tare: DecimalInput },
  after: { gross: DecimalInput; tare: DecimalInput }
): string {
  return toDecimal(after.gross)
    .minus(toDecimal(after.tare))
    .minus(toDecimal(before.gross).minus(toDecimal(before.tare)))
    .toFixed(3);
}

/** "+0.280" / "−0.280" / "0.000": a signed kg change as the screens and the sheet print it. */
export function formatKgChange(change: DecimalInput): string {
  const d = toDecimal(change);
  if (d.isZero()) return "0.000";
  return `${d.isNegative() ? "−" : "+"}${d.abs().toFixed(3)}`;
}

/** "Lot X bag 7: 25.400 → 25.120 kg (−0.280)". Gross before and after; the change is net. */
export function reweighLine(lot: string, bagNo: number, r: BagReweigh): string {
  return `${lot} bag ${bagNo}: ${toDecimal(r.grossBefore).toFixed(3)} → ${toDecimal(r.grossAfter).toFixed(3)} kg (${formatKgChange(r.change)})`;
}
